import { useSuspenseQuery } from '@tanstack/react-query';

import { getRequest } from '@/api/rest/hooks/requests/get';
import { GET_PLOTS_PATH, GetPlotsErrorDto, GetPlotsSuccessDto } from '@/api/rest/resources/types/plot';

import { useOrganizationId } from './useOrganizationId';

type SuccessDto = GetPlotsSuccessDto;
type ErrorDto = GetPlotsErrorDto;

export const usePlotsForOrganization = (
  organizationIdArg?: string,
  filters?: Record<string, unknown> | null,
  limit?: number,
  offset?: number,
  sort?: string,
) => {
  const orgId = useOrganizationId();

  const queryParams = {
    organization: organizationIdArg ?? orgId,
    ...filters,
    limit,
    offset,
    sort,
  };

  const queryFn = async () => {
    const response = await getRequest<SuccessDto>(GET_PLOTS_PATH, undefined, queryParams);
    if (response.results.length > 0) {
      return response;
    }
    /**
     * If there are no results, we default to the first page. This handles the
     * edge case where page no. 5 has only one plot, and if we delete that plot,
     * we end up with a table with no items in it.
     */
    return getRequest<SuccessDto>(GET_PLOTS_PATH, undefined, { ...queryParams, offset: 0 });
  };

  return useSuspenseQuery<SuccessDto, ErrorDto>({
    queryKey: ['plots', 'organization', queryParams],
    queryFn,
    staleTime: 1000 * 60 * 5,
  });
};
